/// <reference path='../StandaloneAriaLogger.ts' />
/// <reference path='../StandaloneOtelLogger.ts' />
/// <reference path='../../ClientApp/typings/shared/WinRt.d.ts' />
import { __awaiter, __generator } from "tslib";
import { AccessTokenDefaultExpiryTimeInMs, BootstrapUtil, CommonUtil, LoggingUtil } from '../shared-inline';
var OfficePwa = /** @class */ (function () {
    function OfficePwa() {
        this.currentMsaUser = null;
        this.currentUserAccount = null;
        this.currentUserPhoto = '';
        this.currentUserPhotoPromise = null;
        this.initializedAccountsPane = false;
        this.isUserInteractionErrorHandled = false;
        this.userInteractionRequired = false;
        this.wamUiPromptPromise = null;
        var serviceWorker = window.navigator.serviceWorker;
        window.diskPreloadRequestMap = new Map();
        window.diskPreloadUserIdStorageKey = 'PwaDiskPreloadUserStorageId';
        window.wamAccessTokenRequests = new Map();
        window.diskPreloadUrlStorageKey = OfficePwa.DiskPreloadStorageKey;
        window.pwaCurrentUserId = null;
        window.windowsDataSecuritySetting = 0 /* Security */;
        window.shellTokenRequestDictionary = {};
        window.pwaResuming = false;
        // Assumes worker would only be installed if page and resources were successfully cached
        window.startedFromServiceWorker = !!(serviceWorker && serviceWorker.controller);
        this.swEnabled = !!serviceWorker;
        if (window.forcedMockAuth === '0') {
            this.requiredMockAuthority = window.forcedMockAuth;
        }
        else if (window.forcedMockAuth === '1') {
            this.requiredMockAuthority = OfficePwa.MsaAuthority;
        }
        else if (window.forcedMockAuth === '2') {
            this.requiredMockAuthority = OfficePwa.AadAuthority;
        }
        this.currentUrl = new URL(window.location.href);
        this.isUwp = navigator && navigator.userAgent.toLowerCase().indexOf('msapphost/') > -1 && typeof (Windows) !== 'undefined';
        this.localSettingsProps = this.isUwp ? Windows.Storage.ApplicationData.current.localSettings.values : null;
        this.elementsDisplayStyleMap = new Map([
            [OfficePwa.UnauthPageId, 'flex'],
            [OfficePwa.OfficeHomeId, 'flex'],
            [OfficePwa.ErrorPageId, 'inline-block'],
            [OfficePwa.VersionUpdatePageId, 'inline-block']
        ]);
        OfficePwa.SessionExpiredToastTemplate = "<toast><visual><binding template=\"ToastGeneric\"><text>Office</text><text>" + OfficePwa.ToastMessageKey + "</text></binding></visual></toast>";
    }
    OfficePwa.prototype.canRender = function () {
        return this.isUwp || this.requiredMockAuthority;
    };
    OfficePwa.prototype.initVars = function () {
        var _this_1 = this;
        // Without this, the app will first refresh to the start path before every activate event
        if (window.MSApp) {
            window.MSApp.pageHandlesAllApplicationActivations(true);
        }
        // Always set session cookie on root path so it's accessible by unauth and auth
        CommonUtil.InitializeCIDSID('OHPWA.SID', '/');
        // Mark initial navigation performance
        this.markPerformance(OfficePwa.InitialPerformanceEvent, 0 /* Security */);
        // Log CorrelationID from page response
        this.getSharedClientStartupConfig().then(function (config) {
            var startParams = {
                PageResponseCid: config ? config.correlationId : ''
            };
            _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'Pwa_Begin', 0 /* Security */, startParams);
        });
        if (this.isUwp) {
            // Fetch windows security settings for logging purposes
            try {
                Windows.System.Profile.PlatformDiagnosticsAndUsageDataSettings.addEventListener('collectionlevelchanged', function () { return _this_1.resetSecurityLevel(); });
                this.resetSecurityLevel();
            }
            catch (_a) {
                // Treat failure to read as highest security setting
            }
            if (!sessionStorage.getItem(OfficePwa.ExistingSessionKey)) {
                // Log time from process start to navigation start
                var splashScreenMarker = 'Splashscreen';
                try {
                    var processDiagnostics = Windows.System.Diagnostics.ProcessDiagnosticInfo.getForCurrentProcess();
                    var processStart = processDiagnostics.processStartTime.getTime();
                    var splashscreenDuration = window.performance.timing.navigationStart - processStart;
                    var perfParams = {
                        Latency: splashscreenDuration.toString(),
                        ItemCategory: 'Inline'
                    };
                    this.logAriaEvent(LoggingUtil.PerformanceDump, splashScreenMarker, 0 /* Security */, perfParams);
                }
                catch (e) {
                    var params = {
                        Error: splashScreenMarker,
                        DebugInfo: e,
                        ItemCategory: 'Inline'
                    };
                    this.logAriaEvent(LoggingUtil.GenericEvent, LoggingUtil.PerformanceMarkFailure, 2 /* Enhanced */, params);
                }
                sessionStorage.setItem(OfficePwa.ExistingSessionKey, 'true');
            }
            // Register for lifecycle events
            try {
                Windows.UI.WebUI.WebUIApplication.addEventListener('activated', function (eventData) { return _this_1.onActivate(eventData); });
                Windows.UI.WebUI.WebUIApplication.addEventListener('suspending', function (eventData) { return _this_1.onSuspend(eventData); });
                Windows.UI.WebUI.WebUIApplication.addEventListener('resuming', function (eventData) { return _this_1.onResume(eventData); });
                Windows.UI.WebUI.WebUIApplication.addEventListener('navigated', function (eventData) { return _this_1.onNavigate(eventData); });
                Windows.UI.WebUI.WebUIApplication.addEventListener('enteredbackground', function (eventData) { return _this_1.onEnterBackground(eventData); });
                Windows.UI.WebUI.WebUIApplication.addEventListener('leavingbackground', function (eventData) { return _this_1.onLeaveBackground(eventData); });
            }
            catch (_b) {
                this.logAriaEvent(LoggingUtil.GenericEvent, 'SubscribeToLifecycleEvents_Error', 2 /* Enhanced */);
            }
            //Install Third Party License Notice
            if (window.thirdPartyNoticeUrl) {
                this.waitForPageLoad().then(function () { return _this_1.installThirdPartyNotice(); });
            }
            // Initialize currentUserId
            if (this.localSettingsProps.hasKey(OfficePwa.CurrentAuthorityKey) && this.localSettingsProps.hasKey(OfficePwa.CurrentAccountIdKey)) {
                var authority = this.localSettingsProps.lookup(OfficePwa.CurrentAuthorityKey);
                window.pwaCurrentUserId = this.getUserIdKey(authority, this.localSettingsProps.lookup(OfficePwa.CurrentAccountIdKey));
            }
        }
        else {
            window.windowsDataSecuritySetting = 3 /* Full */;
        }
    };
    OfficePwa.prototype.initApp = function () {
        var _this_1 = this;
        if (this.isUwp && this.localSettingsProps && this.localSettingsProps.hasKey(OfficePwa.PrelaunchModeKey)) {
            this.setPrelaunchMode();
        }
        if (window.showVersionUpdatePage) {
            this.showVersionUpdatePage();
            return;
        }
        var showUnauthPage = function () {
            _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'Pwa_Unauth_PageView', 0 /* Security */);
            _this_1.initializeUnAuthOTelAndLogPageView({ pageName: 'OfficeHome', pageType: 'SignoutUserView', isIntentional: true, securityThreshold: 0 /* Security */ });
            // Let installing service worker know to stop waiting for auth token
            if (_this_1.swEnabled) {
                navigator.serviceWorker.getRegistration(OfficePwa.BasePwaPath).then(function (registration) {
                    CommonUtil.NotifyInstallingWorkerAppFailed(registration, false);
                });
            }
            _this_1.waitForPageLoad().then(function () {
                _this_1.displayScreen(OfficePwa.UnauthPageId);
                // Assign event listener to button to show account picker
                document.getElementById('sign-in-button').onclick = function () {
                    _this_1.logUnAuthUserAction({ id: 'Pwa_SignIn_Click', area: 'Other', result: 'SignIn', target: 'Office', pageName: 'OfficeHome', pageType: 'SignoutUserView', securityThreshold: 0 /* Security */ });
                    _this_1.showAccountsPane();
                };
                document.getElementById('create-account-link').onclick = function () {
                    _this_1.logUnAuthUserAction({ id: 'Pwa_Create_Account_Click', area: 'Other', result: 'Create', target: 'Account', pageName: 'OfficeHome', pageType: 'SignoutUserView', securityThreshold: 0 /* Security */ });
                };
                _this_1.markPerformance('Pwa_Unauth_Ready', 0 /* Security */, OfficePwa.InitialPerformanceEvent);
                _this_1.flushLogs();
            });
        };
        // In case of mockauth=0, just show the unauth page
        if (this.requiredMockAuthority === '0') {
            showUnauthPage();
            return;
        }
        if (this.localSettingsProps && this.localSettingsProps.hasKey(OfficePwa.SignOutWindowNameKey)) {
            try {
                window.open('about:blank', OfficePwa.SignOutWindowName, 'msHideView=yes').close();
                this.localSettingsProps.remove(OfficePwa.SignOutWindowNameKey);
            }
            catch ( /* Some versions of Edge seem to throw access denied error when opening window with window name passed as param */_a) { /* Some versions of Edge seem to throw access denied error when opening window with window name passed as param */ }
            this.logAriaEvent(LoggingUtil.GenericEvent, 'SignOut_Completed', 2 /* Enhanced */);
        }
        // If we're not in UWP (with mock auth), bootstrap app directly
        if (!this.isUwp) {
            this.bootstrapApp(this.isConsumerAuthority(this.requiredMockAuthority));
        }
        else {
            this.getAccountProvider().then(function (provider) {
                if (provider) {
                    _this_1.bootstrapApp(_this_1.isConsumerAuthority(provider.authority));
                }
                else {
                    showUnauthPage();
                }
            }).catch(function (error) {
                if (error) {
                    _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'GetAccountProvider_Error', 2 /* Enhanced */);
                }
                _this_1.showErrorPage();
            });
        }
    };
    OfficePwa.prototype.initializeGlobalFunctions = function () {
        var _this_1 = this;
        var _this = this;
        window.openThirdPartyNotice = function () {
            var openThirdPartyNoticeFromWeb = function () {
                var noticeUri = new Windows.Foundation.Uri(window.thirdPartyNoticeUrl);
                Windows.System.Launcher.launchUriAsync(noticeUri).then(function (success) {
                    if (!success) {
                        throw new Error();
                    }
                }).then(null, function () {
                    _this.logAriaEvent(LoggingUtil.GenericEvent, 'OpenThirdPartyNotice_Error', 2 /* Enhanced */);
                });
            };
            if (_this.thirdPartyLicenseNotice) {
                Windows.System.Launcher.launchFileAsync(_this.thirdPartyLicenseNotice).then(function (success) {
                    if (!success) {
                        openThirdPartyNoticeFromWeb();
                    }
                }, openThirdPartyNoticeFromWeb);
            }
            else {
                openThirdPartyNoticeFromWeb();
            }
        };
        window.getRememberedAccounts = function () { return __awaiter(_this_1, void 0, void 0, function () {
            var AADAccounts, MSAAccounts;
            return __generator(this, function (_a) {
                _this.rememberedAccounts = [];
                AADAccounts = _this.findAllAccounts(OfficePwa.AadAuthority);
                MSAAccounts = _this.findAllAccounts(OfficePwa.MsaAuthority);
                return [2 /*return*/, Promise.all([AADAccounts, MSAAccounts]).then(function (accountsList) {
                        accountsList.forEach(function (accounts) {
                            if (accounts) {
                                accounts.forEach(function (account) {
                                    _this.rememberedAccounts.push(account);
                                });
                            }
                        });
                        return Promise.resolve(_this.rememberedAccounts);
                    })];
            });
        }); };
        window.switchTo = function (switchToArgs) { return __awaiter(_this_1, void 0, void 0, function () {
            var fetchedTokenTime, nextAccountName, _i, _a, account, selectedUserAccount, accountProvider, error_1;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        fetchedTokenTime = LoggingUtil.CurrentTime();
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 9, , 10]);
                        if (!(switchToArgs.nextAccount && switchToArgs.nextAccount.memberName)) return [3 /*break*/, 8];
                        nextAccountName = switchToArgs.nextAccount.memberName;
                        _i = 0, _a = _this.rememberedAccounts;
                        _b.label = 2;
                    case 2:
                        if (!(_i < _a.length)) return [3 /*break*/, 5];
                        account = _a[_i];
                        if (!(nextAccountName === account.userName)) return [3 /*break*/, 4];
                        return [4 /*yield*/, Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.findAccountAsync(account.webAccountProvider, account.id)];
                    case 3:
                        selectedUserAccount = _b.sent();
                        if (selectedUserAccount) {
                            _this.currentUserAccount = selectedUserAccount;
                        }
                        else {
                            throw new Error('Failed to find account');
                        }
                        return [3 /*break*/, 5];
                    case 4:
                        _i++;
                        return [3 /*break*/, 2];
                    case 5:
                        _this.localSettingsProps.insert(OfficePwa.CurrentAccountIdKey, _this.currentUserAccount.id);
                        return [4 /*yield*/, Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.findAccountProviderAsync(OfficePwa.MsaAadProviderId, _this.currentUserAccount.webAccountProvider.authority)];
                    case 6:
                        accountProvider = _b.sent();
                        _this.saveAccountProvider(accountProvider);
                        _this.setupCurrentUser(accountProvider.authority, _this.currentUserAccount.id);
                        _this.logAriaEvent(LoggingUtil.GenericEvent, 'UMCSwitchAccount_FetchedToken', 2 /* Enhanced */);
                        // Ignore token result here since it will be cached for next page load
                        _this.resetBrowserStorage();
                        return [4 /*yield*/, _this.forceNewServiceWorker()];
                    case 7:
                        _b.sent();
                        _this.logAriaEvent(LoggingUtil.GenericEvent, 'UMCSwitchAccount_Completed', 2 /* Enhanced */, { Latency: Math.ceil(LoggingUtil.CurrentTime() - fetchedTokenTime).toString() });
                        _this.reloadApp();
                        _b.label = 8;
                    case 8: return [3 /*break*/, 10];
                    case 9:
                        error_1 = _b.sent();
                        this.logAriaEvent(LoggingUtil.GenericEvent, 'UMCSwitchAccount_Error', 0 /* Security */, {
                            Latency: Math.ceil(LoggingUtil.CurrentTime() - fetchedTokenTime).toString(),
                            Error: error_1.toString || ''
                        });
                        this.displayScreen(OfficePwa.OfficeHomeId);
                        return [3 /*break*/, 10];
                    case 10: return [2 /*return*/];
                }
            });
        }); };
        window.switchAccount = function () {
            _this.localSettingsProps.insert(OfficePwa.SwitchAccountKey, true);
            _this.logAriaEvent(LoggingUtil.GenericEvent, 'SwitchAccount_Start', 2 /* Enhanced */);
            _this.showAccountsPane(true);
        };
        window.uwpSignOut = function () {
            _this.logAriaEvent(LoggingUtil.GenericEvent, 'SignOut_Started', 2 /* Enhanced */);
            window.open('/pwa/logout', OfficePwa.SignOutWindowName, 'msHideView=yes', false);
            var hasUMCEnabledSessionId = _this.localSettingsProps.hasKey('UMCEnabledSessionId');
            _this.resetBrowserStorage();
            _this.localSettingsProps.clear();
            // Remove when UMC feature is 100% released
            if (hasUMCEnabledSessionId) {
                _this.localSettingsProps.insert('UMCEnabledSessionId', window.overrideSID);
            }
            _this.localSettingsProps.insert(OfficePwa.SignOutWindowNameKey, OfficePwa.SignOutWindowName);
            _this.localSettingsProps.insert(OfficePwa.SignedOutKey, true);
            _this.reloadApp();
        };
        window.getWamAccessToken = function (resource, resourceFriendlyName, clientId, claimsChallenge) {
            return _this.getAccountProvider().then(function (provider) {
                if (!provider) {
                    return Promise.reject('Cannot get user account');
                }
                return _this.getWamTokenResult(resourceFriendlyName, resource, provider, false, clientId, OfficePwa.V2ResourceFriendlyName.includes(resourceFriendlyName), claimsChallenge);
            }).then(function (tokenResult) { return _this.buildAccessTokenResponse(tokenResult, resource, resourceFriendlyName); });
        };
        window.logPwaLifecycleEvent = function (lifecycleEvent) {
            var _a, _b;
            if (typeof ((_b = (_a = MyOffice === null || MyOffice === void 0 ? void 0 : MyOffice.RuntimeComponents) === null || _a === void 0 ? void 0 : _a.TraceLog) === null || _b === void 0 ? void 0 : _b.logLifeCycleTrace) === 'function') {
                MyOffice.RuntimeComponents.TraceLog.logLifeCycleTrace(lifecycleEvent, '', '', 0, 0);
            }
        };
    };
    OfficePwa.prototype.getAccountProvider = function () {
        var _this_1 = this;
        if (this.currentAccountProvider) {
            return Promise.resolve(this.currentAccountProvider);
        }
        else if (this.localSettingsProps.hasKey(OfficePwa.SignedOutKey)) {
            return Promise.resolve(null);
        }
        return new Promise(function (resolve, _reject) {
            var providerId = _this_1.localSettingsProps.lookup(OfficePwa.CurrentUserProviderIdKey);
            var authority = _this_1.localSettingsProps.lookup(OfficePwa.CurrentAuthorityKey);
            var getProviderPromise;
            if (providerId && authority) {
                getProviderPromise = Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.findAccountProviderAsync(providerId, authority);
            }
            else {
                getProviderPromise = Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.findAccountProviderAsync(OfficePwa.LocalProviderId);
            }
            getProviderPromise.then(function (provider) {
                if (provider && (provider.authority === OfficePwa.MsaAuthority || provider.authority === OfficePwa.AadAuthority) && (!_this_1.requiredMockAuthority || provider.authority === _this_1.requiredMockAuthority)) {
                    if (provider.id === OfficePwa.LocalProviderId) {
                        // When using local provider ID, we cannot use the provider object in getCurrentAccount() as it would return null
                        return Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.findAccountProviderAsync(OfficePwa.MsaAadProviderId, provider.authority);
                    }
                    else {
                        return provider;
                    }
                }
                else {
                    return null;
                }
            }).then(function (provider) {
                if (provider) {
                    _this_1.saveAccountProvider(provider);
                }
                resolve(provider);
            });
        });
    };
    OfficePwa.prototype.buildAccessTokenResponse = function (tokenResult, resource, resourceFriendlyName) {
        if (!this.checkWamTokenResult(tokenResult)) {
            return null;
        }
        var tokenResponse = tokenResult.responseData[0];
        var provider = tokenResponse.webAccount.webAccountProvider;
        var expiry = tokenResponse.properties && tokenResponse.properties.hasKey('exp') && (parseInt(tokenResponse.properties.lookup('exp')) * 1000); // Only set for JWT tokens.
        var isConsumer = this.isConsumerAuthority(provider.authority);
        return {
            success: true,
            token: tokenResult.responseData[0].token,
            expiryMs: expiry || (!isConsumer && this.getDefaultExpiry()),
            format: (!provider || !isConsumer || resource === OfficePwa.OfficeHomeV2Resource || resourceFriendlyName === 'Substrate') ? "JWT" /* JWT */ : "RPSCompact" /* RPSCompact */,
            source: "WAM" /* WAM */
        };
    };
    OfficePwa.prototype.getDefaultExpiry = function () {
        return new Date().getTime() + AccessTokenDefaultExpiryTimeInMs; // 1 hour in the future
    };
    OfficePwa.prototype.isAsianCulture = function (cultureName) {
        var asianCultures = ['ja', 'ko', 'zh'];
        if (cultureName) {
            return asianCultures.some(function (asianCulture) { return cultureName.indexOf(asianCulture) === 0; });
        }
        return false;
    };
    // Same implementation as Utils.GetUserDisplayName
    OfficePwa.prototype.getUserDisplayName = function (cultureName) {
        var name = '';
        if (this.currentUserAccount) {
            var firstName = this.currentUserAccount.properties.hasKey('FirstName') ? this.currentUserAccount.properties.lookup('FirstName') : '';
            var lastName = this.currentUserAccount.properties.hasKey('LastName') ? this.currentUserAccount.properties.lookup('LastName') : '';
            if (firstName && lastName) {
                name = this.isAsianCulture(cultureName) ? lastName + firstName : firstName + ' ' + lastName;
            }
            else {
                name = firstName ? firstName : (lastName ? lastName : this.currentUserAccount.userName);
            }
        }
        return name;
    };
    OfficePwa.prototype.getUserIdKey = function (authority, accountId) {
        return authority + "_" + accountId;
    };
    OfficePwa.prototype.getCurrentAccount = function (accountProvider) {
        var _this_1 = this;
        if (this.currentUserAccount) {
            return Promise.resolve(this.currentUserAccount);
        }
        if (!accountProvider || !this.localSettingsProps.hasKey(OfficePwa.CurrentAccountIdKey)) {
            return Promise.reject();
        }
        return this.findAccountAsync(accountProvider).then(function (acc) {
            _this_1.currentUserAccount = acc;
            _this_1.setupCurrentUser(accountProvider.authority, acc.id);
            return acc;
        });
    };
    OfficePwa.prototype.findAccountAsync = function (accountProvider) {
        var _this_1 = this;
        var findAccountStart = LoggingUtil.CurrentTime();
        var findAccount = new Promise(function (resolve, reject) {
            Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.findAccountAsync(accountProvider, _this_1.localSettingsProps.lookup(OfficePwa.CurrentAccountIdKey)).then(resolve, reject);
        });
        var logFindAccount = function (success, error) {
            _this_1.logRequest('FindAccount_Request', Math.ceil(LoggingUtil.CurrentTime() - findAccountStart), success, 2 /* Enhanced */, false, error);
        };
        return findAccount.then(function (account) {
            if (!account) {
                var error = 'Failed to find account';
                logFindAccount(false, error);
                _this_1.localSettingsProps.remove(OfficePwa.CurrentAccountIdKey);
                return Promise.reject(error);
            }
            logFindAccount(true);
            return Promise.resolve(account);
        }, function (error) {
            logFindAccount(false, error);
            _this_1.localSettingsProps.remove(OfficePwa.CurrentAccountIdKey);
            return Promise.reject(error);
        });
    };
    OfficePwa.prototype.cleanUpShellAndConfigCallbacks = function () {
        this.shellAndConfigPromiseResolver = null;
        this.shellAndConfigPromiseRejecter = null;
    };
    OfficePwa.prototype.logAriaEvent = function (streamName, eventName, securityThreshold, eventParams, metrics) {
        eventParams = eventParams || {};
        if (this.currentAccountProvider) {
            eventParams['AuthType'] = this.isConsumerAuthority(this.currentAccountProvider.authority) ? 'MSA' : 'OrgId';
        }
        LoggingUtil.LogEvent(streamName, eventName, securityThreshold, eventParams, metrics);
    };
    OfficePwa.prototype.logRequest = function (requestName, duration, success, securityThreshold, fromCache, error, params) {
        var _a;
        params = params || {};
        params['Latency'] = duration.toString();
        params['State'] = success ? 'Succeeded' : 'Failed';
        params['FromServiceWorkerCache'] = (!!fromCache).toString();
        if (error) {
            params['Error'] = (_a = error.toString()) === null || _a === void 0 ? void 0 : _a.replace(LoggingUtil.EmailRegex, 'email_removed');
        }
        this.logAriaEvent(LoggingUtil.GenericRequest, requestName, securityThreshold, params);
    };
    OfficePwa.prototype.markPerformance = function (mark, securityThreshold, measureFrom) {
        LoggingUtil.MarkPerformance(mark, measureFrom, securityThreshold, window.windowsDataSecuritySetting, { Page: OfficePwa.PwaPage });
    };
    OfficePwa.prototype.checkWamTokenResult = function (tokenResult) {
        return tokenResult && tokenResult.responseStatus === Windows.Security.Authentication.Web.Core.WebTokenRequestStatus.success && tokenResult.responseData && tokenResult.responseData.length > 0;
    };
    OfficePwa.prototype.getWamTokenResult = function (resourceFriendlyName, resourceUrl, accountProvider, cacheRequest, clientId, isVersion20, claimsChallenge) {
        var _this_1 = this;
        var _a;
        var fetchWamTokenStart = LoggingUtil.CurrentTime();
        var fetchWamTokenError = '';
        var wamTokenRequest;
        var isConsumer = this.isConsumerAuthority(accountProvider.authority);
        if (isConsumer) {
            wamTokenRequest = new Windows.Security.Authentication.Web.Core.WebTokenRequest(accountProvider, resourceUrl, isVersion20 ? OfficePwa.AppId : 'none');
            if (isVersion20) {
                wamTokenRequest.properties.insert('api-version', '2.0');
            }
        }
        else {
            var scope = resourceFriendlyName === OfficePwa.OfficeApiResource ? 'openid' : '';
            // if clientId is passed in, use that for wam call, otherwise use office app Id for wam call
            wamTokenRequest = new Windows.Security.Authentication.Web.Core.WebTokenRequest(accountProvider, scope, clientId || OfficePwa.AppId);
            if (clientId) {
                wamTokenRequest.properties.insert('api-version', '2.0');
            }
            wamTokenRequest.properties.insert('resource', resourceUrl);
            if (claimsChallenge && ((_a = this.capabilitiesConfig) === null || _a === void 0 ? void 0 : _a.claimsChallengeEnabled)) {
                try {
                    var decodedClaimsChallenge = atob(claimsChallenge);
                    wamTokenRequest.properties.insert('claims', decodedClaimsChallenge);
                }
                catch (err) {
                    this.logAriaEvent(LoggingUtil.GenericEvent, 'DecodeClaimsChallenge_Error', 1 /* Basic */, {
                        Error: typeof err === 'string' ? err : (err ? err.message : '')
                    });
                }
            }
        }
        var tokenPromise = new Promise(function (resolve, reject) {
            if (!wamTokenRequest) {
                fetchWamTokenError = 'InvalidTokenRequest';
                reject();
                return;
            }
            var requestTokenAsync = function (tokenRequest, useCurrentAccount) {
                if (useCurrentAccount === void 0) { useCurrentAccount = false; }
                _this_1.getWamToken(resourceFriendlyName, tokenRequest, useCurrentAccount).then(resolve, reject);
            };
            if (_this_1.localSettingsProps.hasKey(OfficePwa.SwitchAccountKey) || _this_1.localSettingsProps.hasKey(OfficePwa.SwitchToSelectedAccountKey)) {
                requestTokenAsync(wamTokenRequest);
            }
            else {
                _this_1.getCurrentAccount(accountProvider).then(function (account) {
                    return _this_1.getWamTokenSilently(resourceFriendlyName, wamTokenRequest, account);
                }).then(function (tokenResult) {
                    resolve(tokenResult);
                }).catch(function () {
                    requestTokenAsync(wamTokenRequest, true);
                });
            }
        });
        var success = true;
        tokenPromise.catch(function () {
            success = false;
        }).then(function () {
            var params = {
                Resource: resourceFriendlyName
            };
            _this_1.logRequest('GetWamTokenResult_Request', Math.ceil(LoggingUtil.CurrentTime() - fetchWamTokenStart), success, 0 /* Security */, false, fetchWamTokenError, params);
        });
        if (cacheRequest) {
            var accessTokenRequest = {
                pending: true,
                result: tokenPromise.then(function (result) { return _this_1.buildAccessTokenResponse(result, resourceUrl, resourceFriendlyName); })
            };
            window.wamAccessTokenRequests.set(resourceUrl, accessTokenRequest);
        }
        return tokenPromise;
    };
    OfficePwa.prototype.getWamToken = function (resourceFriendlyName, tokenRequest, useCurrentAccount) {
        var _this_1 = this;
        if (useCurrentAccount === void 0) { useCurrentAccount = false; }
        var getInstrumentedTokenFetch = function () {
            var tokenFetch = new Promise(function (resolve, reject) {
                var requestTokenAsync = useCurrentAccount && _this_1.currentUserAccount ?
                    Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.requestTokenAsync(tokenRequest, _this_1.currentUserAccount) :
                    Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.requestTokenAsync(tokenRequest);
                requestTokenAsync.then(function (tokenResult) {
                    if (tokenResult &&
                        tokenResult.responseStatus === Windows.Security.Authentication.Web.Core.WebTokenRequestStatus.userCancel &&
                        _this_1.userInteractionRequired) {
                        // Don't handle the error and wait until sign out + reload happens
                        if (_this_1.isUserInteractionErrorHandled) {
                            return;
                        }
                        if (OfficePwa.SessionExpiredToastMessage) {
                            try {
                                var xmlDoc = new Windows.Data.Xml.Dom.XmlDocument();
                                xmlDoc.loadXml(OfficePwa.SessionExpiredToastMessage);
                                var toast = new Windows.UI.Notifications.ToastNotification(xmlDoc);
                                var toastNotifier = Windows.UI.Notifications.ToastNotificationManager.createToastNotifier();
                                toastNotifier.show(toast);
                                _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'UserSessionExpired', 0 /* Security */);
                            }
                            catch (err) {
                                _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'CreateSessionExpiredToast_Error', 0 /* Security */, {
                                    Error: typeof err === 'string' ? err : (err ? err.message : '')
                                });
                            }
                        }
                        else {
                            _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'MissingSessionExpiredToastMessage', 0 /* Security */);
                        }
                        // Force user to sign out and show toast message
                        _this_1.isUserInteractionErrorHandled = true;
                        window.uwpSignOut();
                    }
                    else {
                        resolve(tokenResult);
                    }
                }, reject);
            });
            return _this_1.instrumentedTokenFetch(tokenFetch, 'GetWamToken_Request', resourceFriendlyName);
        };
        if (!this.wamUiPromptPromise) {
            this.wamUiPromptPromise = getInstrumentedTokenFetch();
        }
        else {
            this.wamUiPromptPromise = this.wamUiPromptPromise.then(getInstrumentedTokenFetch, getInstrumentedTokenFetch);
        }
        return this.wamUiPromptPromise;
    };
    OfficePwa.prototype.getWamTokenSilently = function (resourceFriendlyName, tokenRequest, account) {
        var _this_1 = this;
        var silentTokenFetch = new Promise(function (resolve, reject) {
            // If account is null, getTokenSilentlyAsync throws an error rather than ignoring it
            var silentFetch = account ?
                Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.getTokenSilentlyAsync(tokenRequest, account) :
                Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.getTokenSilentlyAsync(tokenRequest);
            silentFetch.then(function (tokenResult) {
                // If error code is 2156265478 (ONL_E_FORCESIGNIN) or 2156265484 (ONL_E_ACTION_REQUIRED), mark for potential force sign out
                // TODO: handle infinite prompts too
                if (window.enableUIRForceSignOut &&
                    tokenResult &&
                    tokenResult.responseStatus === Windows.Security.Authentication.Web.Core.WebTokenRequestStatus.userInteractionRequired &&
                    tokenResult.responseError &&
                    (tokenResult.responseError.errorCode === 2156265478 ||
                        tokenResult.responseError.errorCode === 2156265484)) {
                    _this_1.userInteractionRequired = true;
                }
                resolve(tokenResult);
            }, reject);
        });
        return this.instrumentedTokenFetch(silentTokenFetch, 'GetWamTokenSilently_Request', resourceFriendlyName);
    };
    OfficePwa.prototype.instrumentedTokenFetch = function (tokenPromise, eventName, resourceFriendlyName) {
        var _this_1 = this;
        var getTokenStart = LoggingUtil.CurrentTime();
        var logTokenFetch = function (success, status, error) {
            var params = {
                Resource: resourceFriendlyName,
                DebugInfo: status.toString()
            };
            _this_1.logRequest(eventName, Math.ceil(LoggingUtil.CurrentTime() - getTokenStart), success, 0 /* Security */, false, error, params);
        };
        return tokenPromise.then(function (tokenResult) {
            if (!_this_1.checkWamTokenResult(tokenResult)) {
                var respError = tokenResult && tokenResult.responseError;
                logTokenFetch(false, tokenResult && tokenResult.responseStatus, respError ? respError.errorCode + ": " + respError.errorMessage : '');
                return Promise.reject(tokenResult);
            }
            logTokenFetch(true, tokenResult.responseStatus);
            return Promise.resolve(tokenResult);
        }, function (error) {
            logTokenFetch(false, -1, error);
            return Promise.reject();
        });
    };
    OfficePwa.prototype.processShellConfigApiData = function (apiResponse, isConsumer) {
        var _this_1 = this;
        var _a, _b, _c, _d, _e, _f;
        var parsedApiResponse = null;
        try {
            // V2 API response is an object instead of string, so keeping the try catch block here while we transition
            parsedApiResponse = JSON.parse(apiResponse);
        }
        catch (_g) {
            parsedApiResponse = apiResponse;
        }
        if (parsedApiResponse) {
            this.configRetrieved = true;
            var parsedStartupConfig = parsedApiResponse.clientStartupConfiguration ? JSON.parse(parsedApiResponse.clientStartupConfiguration) : null;
            var successParams = {
                ShellAndConfigCid: parsedStartupConfig ? parsedStartupConfig.sharedClientStartupContext.correlationId : ''
            };
            this.logAriaEvent(LoggingUtil.GenericEvent, 'ShellAndConfigApi_Response', 0 /* Security */, successParams);
            if (parsedApiResponse.capabilities) {
                this.capabilitiesConfig = JSON.parse(parsedApiResponse.capabilities);
                // When the universalMeControlPWAEnabled flight is enabled through ECS for a user, we store the sessionID for the enabled, with which we enable UMC for all accounts the user switches to, in that session.
                // UniversalMeControlSafetyFlightEnabled is a safety flight to handle case when the UMC will be enabled due to valid sessionID but we still want to disable it.
                if (this.capabilitiesConfig.universalMeControlSafetyFlightEnabled) {
                    // Update universalMeControlPWAEnabled value based on if universalMeControlPWAEnabled is enabled from ECS, findAllAccountsAsync API is available
                    // and if in WebView2 version of app, then build number is equal to or greater than 18.2204.1201.0
                    this.capabilitiesConfig.universalMeControlPWAEnabled = this.capabilitiesConfig.universalMeControlPWAEnabled && ((_a = Windows === null || Windows === void 0 ? void 0 : Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager) === null || _a === void 0 ? void 0 : _a.findAllAccountsAsync)
                        && ((_f = (_e = (_d = (_c = (_b = Windows.Foundation) === null || _b === void 0 ? void 0 : _b.Metadata) === null || _c === void 0 ? void 0 : _c.ApiInformation) === null || _d === void 0 ? void 0 : _d.isMethodPresent) === null || _e === void 0 ? void 0 : _e.call(_d, 'Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager', 'FindAllAccountsAsync')) !== null && _f !== void 0 ? _f : false)
                        && this.isValidAppVersion({ build: 1201, major: 18, minor: 2204, revision: 0 });
                    if (this.capabilitiesConfig.universalMeControlPWAEnabled) {
                        this.localSettingsProps.insert('UMCEnabledSessionId', window.overrideSID);
                    }
                    else if (this.localSettingsProps.lookup('UMCEnabledSessionId') === window.overrideSID) {
                        this.capabilitiesConfig.universalMeControlPWAEnabled = true;
                    }
                    else {
                        this.localSettingsProps.remove('UMCEnabledSessionId');
                    }
                }
            }
            if (parsedApiResponse.pwaConfig) {
                var parsedPwaConfig_1 = JSON.parse(parsedApiResponse.pwaConfig);
                if (parsedPwaConfig_1) {
                    if (parsedPwaConfig_1.resourceUrlMap && this.isUwp) {
                        var resourceMap = Object.entries(parsedPwaConfig_1.resourceUrlMap);
                        var deferredTokens_1 = new Set(parsedPwaConfig_1.deferredTokens || []);
                        resourceMap.forEach(function (_a) {
                            var resourceKey = _a[0], resource = _a[1];
                            if (!deferredTokens_1.has(resourceKey)) {
                                _this_1.getWamTokenResult(resourceKey, resource, _this_1.currentAccountProvider, true, undefined, OfficePwa.V2ResourceFriendlyName.includes(resourceKey));
                            }
                            // Following is needed to provide tokens for Commercial React Shell in PWA. Update the resource names in OfficePwaApiModel if changed.
                            if (resourceKey === 'WCSS' || resourceKey === 'Graph') {
                                window.shellTokenRequestDictionary[resource] = function () { return window.wamAccessTokenRequests.has(resource) ?
                                    window.wamAccessTokenRequests.get(resource).result.then(function (tokenResponse) { return tokenResponse ? tokenResponse.token : null; }) :
                                    _this_1.getWamTokenResult(resourceKey, resource, _this_1.currentAccountProvider, true, undefined, OfficePwa.V2ResourceFriendlyName.includes(resourceKey))
                                        .then(function (result) { return _this_1.checkWamTokenResult(result) ? result.responseData[0].token : null; }); };
                            }
                        });
                    }
                    if (parsedPwaConfig_1.sessionExpiredToastMessage) {
                        OfficePwa.SessionExpiredToastMessage = OfficePwa.SessionExpiredToastTemplate.replace(OfficePwa.ToastMessageKey, parsedPwaConfig_1.sessionExpiredToastMessage);
                    }
                    // Initialize fluent icons
                    if (this.capabilitiesConfig && this.capabilitiesConfig['fluentIconFontEnabled'] && parsedPwaConfig_1.iconFontUrl) {
                        var fluentLink = document.createElement('link');
                        fluentLink.rel = 'stylesheet';
                        fluentLink.type = 'text/css';
                        fluentLink.crossOrigin = 'anonymous';
                        fluentLink.id = OfficePwa.IconFontElementId;
                        if (parsedPwaConfig_1.iconFontIntegrity) {
                            fluentLink.integrity = parsedPwaConfig_1.iconFontIntegrity;
                        }
                        fluentLink.onerror = function () {
                            var _a;
                            var params = (_a = {},
                                _a[OfficePwa.InstrumentationParamAction] = parsedPwaConfig_1.iconFontUrl,
                                _a);
                            _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'Icon_Font_Load_Error', 0 /* Security */, params);
                        };
                        fluentLink.href = parsedPwaConfig_1.iconFontUrl;
                        var fabricLink = document.getElementById(OfficePwa.IconFontElementId);
                        var head = fabricLink.parentNode;
                        if (head && fabricLink) {
                            head.replaceChild(fluentLink, fabricLink);
                        }
                    }
                }
            }
            // initialize Shell
            if (parsedApiResponse.navBarData) {
                var navBarData_1 = JSON.parse(parsedApiResponse.navBarData);
                window.navBarData = navBarData_1;
            }
            if (isConsumer && this.isUwp) {
                this.currentMsaUser = {
                    upn: this.currentUserAccount.userName,
                    puid: this.currentUserAccount.properties.hasKey('UID') ? this.currentUserAccount.properties.lookup('UID') : '',
                    userDisplayName: this.getUserDisplayName(Windows.ApplicationModel.Resources.Core.ResourceManager.current.defaultContext.languages[0])
                };
                window.userPrincipalName = this.currentUserAccount.userName;
            }
            if (parsedApiResponse.reactShellConfiguration) {
                this.reactShellConfig = JSON.parse(parsedApiResponse.reactShellConfiguration);
            }
            // Start bootstrapping Shell
            if (this.shellShimReady && !this.reactShellBootstrapped) {
                this.waitForPageLoad().then(function () { return _this_1.bootstrapReactShell(); });
            }
            // initialize OCV
            window.OfficeBrowserFeedback = window.OfficeBrowserFeedback || {};
        }
        else {
            throw new Error('Empty shell and config response');
        }
        return parsedApiResponse;
    };
    OfficePwa.prototype.requestConfigAndShellApiData = function (isConsumer, fromCache, accessToken) {
        var _this_1 = this;
        var controller = this.swEnabled ? window.navigator.serviceWorker.controller : null;
        var headers = new Headers();
        return this.getSharedClientStartupConfig().then(function (config) {
            // When v2 token setting is enabled, we still fallback to the v1 auth for MSA login that doesn't
            // specify a user. In this case accessToken is a RPS ticket starting with 't='. In this case, don't attach
            // the token to the header since our service can't handle RPS tickets.
            if ((!isConsumer || config.pwaV2TokenEnabled) && accessToken && accessToken.indexOf('t=') !== 0) {
                headers.append('Authorization', "Bearer " + accessToken);
            }
            if (controller) {
                headers.append('X-SW-OHP-FetchStrategy', fromCache ? 'cache-only' : 'network-first');
                if (window.pwaCurrentUserId) {
                    headers.append('X-SW-OHP-UserCacheId', window.pwaCurrentUserId);
                }
            }
            headers.append('X-OfficeHome-CorrelationId', window.overrideCID);
            var shellAndConfigInit = {
                method: 'GET',
                headers: headers,
                cache: 'default',
                credentials: 'include'
            };
            var url = new URL(_this_1.currentUrl.origin + "/" + (window.configApiPath || 'api/shellandconfigdata/pwa') + _this_1.currentUrl.search);
            CommonUtil.SetQueryParam(url, _this_1.requiredMockAuthority ? 'mockauth' : 'auth', isConsumer ? '1' : '2');
            CommonUtil.SetQueryParam(url, 'workload', 'pwa');
            var shellAndConfigRequest = new Request(url.toString(), shellAndConfigInit);
            var shellAndConfigStart = LoggingUtil.CurrentTime();
            var shellAndConfigRequestPromise = fetch(shellAndConfigRequest);
            var logShellAndConfig = function (success, statusCode) {
                var params = {
                    HttpCode: statusCode.toString(),
                    DebugInfo: url.href // TODO: Stop logging after auth mismatch investigation (estimated 4/1)
                };
                _this_1.logRequest('ShellAndConfig_Request', Math.ceil(LoggingUtil.CurrentTime() - shellAndConfigStart), success, 0 /* Security */, fromCache, '', params);
            };
            shellAndConfigRequestPromise.then(function (response) {
                logShellAndConfig(response.ok, response.status);
            }, function () {
                logShellAndConfig(false, fromCache ? 709 /* Cache miss from http-status-code.enum */ : 700 /* Unexpected Response */);
            });
            return shellAndConfigRequestPromise
                .then(function (response) {
                if (response.status >= 200 && response.status < 300) {
                    return response;
                }
                else {
                    throw Error(response.statusText);
                }
            })
                .then(function (response) { return response.json(); });
        });
    };
    // During switch account, we don't want to cache token because we will eventually refresh the page and everything in memory will be destroyed
    // On page unload, we have requests to user storage API that will access token cache, so we want to avoid that
    OfficePwa.prototype.getOfficeToken = function (provider, cacheOfficeToken) {
        var _this_1 = this;
        if (cacheOfficeToken === void 0) { cacheOfficeToken = true; }
        // Remove getSharedClientStartupConfig call when pwaV2TokenEnabled ECS flight has been released
        return this.getSharedClientStartupConfig().then(function (config) {
            var isV2 = false;
            var resource = (function () {
                var isConsumer = _this_1.isConsumerAuthority(provider.authority);
                // V2 AppID for MSA & AAD auth
                // Disable v2 token fetch for MSA on UWP where the user selected to add a new account.
                // In this case, provider.user is an error object and doesn't have the nonRoamableId property.
                // If an account was selected from the picker, then provider.user will have a nonRoamableId.
                // In UWP, accessing the provider.user property can throw an error if a user was not selected on the picker screen.
                // Therefore, use try/catch to catch that error and continue with checking the user.
                // In WebView2, provider.user property does not exist, but since we have a separate check for WebView2,
                // it should not affect the logic.
                // In UWP, getOfficeToken gets called twice on an account switch: first time provider.user is set, second time it is not
                // set, but account is cached in localSettingsProps. If either exist we can use the v2 token.
                // TODO: remove extra checks once this signin issue is resolved.
                var user = null;
                try {
                    user = provider.user;
                }
                catch (e) {
                    console.log('error accessing user property. Proceeding as if user property is null.');
                }
                var isWebView2 = typeof window.Office !== 'undefined';
                var currentAccountIsCached = _this_1.localSettingsProps.hasKey(OfficePwa.CurrentAccountIdKey);
                var accountSwitchInProgress = _this_1.localSettingsProps.hasKey(OfficePwa.SwitchAccountKey) || _this_1.localSettingsProps.hasKey(OfficePwa.SwitchToSelectedAccountKey);
                if ((config === null || config === void 0 ? void 0 : config.pwaV2TokenEnabled) && (!isConsumer || isWebView2 || !!(user === null || user === void 0 ? void 0 : user.nonRoamableId) || (currentAccountIsCached && !accountSwitchInProgress))) {
                    isV2 = true;
                    return OfficePwa.OfficeHomeV2Resource;
                }
                // MSA auth
                if (isConsumer) {
                    _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'WAM_V1_MSA_Fallback', 2 /* Enhanced */, {
                        DebugInfo: "user: " + !!user + ". currentUserAccount: " + currentAccountIsCached + ". IsWebView2: " + isWebView2
                    });
                    return 'service::office.com::MBI_SSL';
                }
                // V1 AppID for AAD auth
                return '4345a7b9-9a63-4910-a426-35363201d503';
            })();
            return _this_1.getWamTokenResult(OfficePwa.OfficeApiResource, resource, provider, cacheOfficeToken, undefined, isV2).then(function (tokenResult) {
                // Store current account
                _this_1.currentUserAccount = tokenResult.responseData[0].webAccount;
                _this_1.localSettingsProps.insert(OfficePwa.CurrentAccountIdKey, _this_1.currentUserAccount.id);
                _this_1.setupCurrentUser(provider.authority, _this_1.currentUserAccount.id);
                return tokenResult;
            });
        });
    };
    OfficePwa.prototype.saveAccountProvider = function (provider) {
        this.currentAccountProvider = provider;
        this.localSettingsProps.insert(OfficePwa.CurrentUserProviderIdKey, provider.id);
        this.localSettingsProps.insert(OfficePwa.CurrentAuthorityKey, provider.authority);
    };
    OfficePwa.prototype.clearSavedProvider = function () {
        this.currentAccountProvider = null;
        this.localSettingsProps.remove(OfficePwa.CurrentUserProviderIdKey);
        this.localSettingsProps.remove(OfficePwa.CurrentAuthorityKey);
    };
    OfficePwa.prototype.getCurrentUserPhoto = function () {
        var _this_1 = this;
        //TODO: webAccount.getPictureAsnyc works for msa only. Update when method to get commercial photo is figured out.
        if (!this.currentUserAccount || !this.isConsumerAuthority(this.currentUserAccount.webAccountProvider.authority) || this.localSettingsProps.hasKey(OfficePwa.SwitchAccountKey)) {
            return;
        }
        if (this.currentUserPhotoPromise) {
            return;
        }
        this.currentUserPhotoPromise = new Promise(function (resolve) {
            _this_1.currentUserAccount.getPictureAsync(Windows.Security.Credentials.WebAccountPictureSize.size208x208)
                .then(function (picStream) {
                var buffer = new Windows.Storage.Streams.Buffer(picStream.size);
                return picStream.readAsync(buffer, picStream.size, Windows.Storage.Streams.InputStreamOptions.readAhead);
            })
                .then(function (buf) {
                _this_1.currentUserPhoto = Windows.Security.Cryptography.CryptographicBuffer.encodeToBase64String(buf);
            })
                .done(function () { return resolve(_this_1.currentUserPhoto); }, function () {
                _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'GetUserPhoto_Error', 2 /* Enhanced */);
                resolve('');
            });
        });
    };
    OfficePwa.prototype.handleShellConfigError = function (error) {
        var errorParams = {
            Error: typeof error === 'string' ? error : error ? error.name : '',
            DebugInfo: error ? error.message : ''
        };
        this.logAriaEvent(LoggingUtil.GenericEvent, 'ShellAndConfigData_Error', 0 /* Security */, errorParams);
        this.shellAndConfigPromiseRejecter('Failed to fetch config data');
        this.cleanUpShellAndConfigCallbacks();
    };
    OfficePwa.prototype.fetchAndHandleShellAndConfigResponse = function (isConsumer, token) {
        var _this_1 = this;
        return this.requestConfigAndShellApiData(isConsumer, false, token).then(function (apiResponse) {
            var configData = _this_1.processShellConfigApiData(apiResponse, isConsumer);
            _this_1.shellAndConfigPromiseResolver(configData);
            _this_1.cleanUpShellAndConfigCallbacks();
        }).catch(function (err) { return _this_1.handleShellConfigError(err); });
    };
    OfficePwa.prototype.fetchTokenAndCallShellAndConfigApi = function (provider) {
        var _this_1 = this;
        if (!this.shellAndConfigPromiseResolver || !this.shellAndConfigPromiseRejecter) {
            this.logAriaEvent(LoggingUtil.GenericEvent, 'MissingPromiseResolveReject_Error', 0 /* Security */);
            return;
        }
        var getTokenErrorMessage = function (tokenResult) {
            return "Error fetching token (" + (tokenResult ? tokenResult.responseStatus : '') + ") " + (tokenResult && tokenResult.responseError ? tokenResult.responseError.errorCode : '');
        };
        var isConsumer = this.isConsumerAuthority(provider.authority);
        var promiseFulfilled = false;
        var cacheAvailable = window.pwaCurrentUserId && this.swEnabled && window.navigator.serviceWorker.controller;
        var cachedFlow = cacheAvailable ?
            // Populate currentUserAccount before we get shell/config data
            this.getCurrentAccount(provider).then(function (_) { return _this_1.requestConfigAndShellApiData(isConsumer, true /* No token required for cache-only */); }) :
            null;
        var networkFlow = isConsumer ?
            this.getSharedClientStartupConfig().then(function (config) {
                // TODO: simplify this block of code once pwaV2TokenEnabled is enabled by default
                if (config === null || config === void 0 ? void 0 : config.pwaV2TokenEnabled) {
                    return _this_1.getOfficeToken(provider).then(function (tokenResult) { return _this_1.requestConfigAndShellApiData(true, false, tokenResult.responseData[0].token); });
                }
                // If an MSA user is auto signed in through Windows, getCurrentAccount would reject because we have not fetched any token to retrieve a WebAccount
                // We should then catch the promise reject and fetch a token even if we don't really need it for the API call
                return _this_1.getCurrentAccount(provider).catch(function (_) { return _this_1.getOfficeToken(provider); }).then(function (_) { return _this_1.requestConfigAndShellApiData(true, false); });
            }) :
            this.getOfficeToken(provider).then(function (tokenResult) { return _this_1.requestConfigAndShellApiData(false, false, tokenResult.responseData[0].token); } /* Call shell and config API */);
        var errorHandler = function (error) {
            // Checking if error is due to fetching token
            if (error && error.responseStatus) {
                // We call this function during auto sign in scenario (user signed in previously or user signed in to Windows using AAD/MSA account). It is possible that
                // the IDs that we cache from previous sign-ins have changed or WAM token fetch fails for whatever reasons. When this happens, we should just
                // clear cached user IDs and show the unauth page so users can sign in again.
                _this_1.clearSavedProvider();
                _this_1.localSettingsProps.remove(OfficePwa.CurrentAccountIdKey);
                _this_1.localSettingsProps.insert(OfficePwa.SignedOutKey, true);
                var tokenResult = error;
                var instrumentationParam = {
                    Message: getTokenErrorMessage(tokenResult)
                };
                _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'FetchOfficeToken_Error', 0 /* Security */, instrumentationParam);
                // Reloading app instead of showing unauth page directly because we have already started initializing PWA at this point (window.shellAndConfigResponse
                // has been set)
                _this_1.reloadApp();
                return;
            }
            _this_1.handleShellConfigError(error);
        };
        // Hit Network
        networkFlow.then(function (result) {
            if (!promiseFulfilled) {
                var configData = _this_1.processShellConfigApiData(result, isConsumer);
                _this_1.shellAndConfigPromiseResolver(configData);
                _this_1.cleanUpShellAndConfigCallbacks();
                promiseFulfilled = true;
            }
        }).catch(function (ex) {
            // If network call fails, check if we were able to use the cache
            if (!cacheAvailable && !promiseFulfilled) {
                // If cache is unavailable, then we have to fail the app startup
                errorHandler(ex);
                promiseFulfilled = true;
            }
        });
        if (cachedFlow) {
            // Hit the cache for startup information
            cachedFlow.then(function (cachedResult) {
                if (!promiseFulfilled) {
                    // On cache hit, we can start the app
                    var configData = _this_1.processShellConfigApiData(cachedResult, isConsumer);
                    _this_1.shellAndConfigPromiseResolver(configData);
                    _this_1.cleanUpShellAndConfigCallbacks();
                    promiseFulfilled = true;
                }
            }).catch(function () {
                var handleError = function () {
                    // If cache fails, and network fails, we have to fail the app startup
                    networkFlow.catch(function (ex) {
                        errorHandler(ex);
                        promiseFulfilled = true;
                    });
                };
                if (window.enableCacheMissReload && !promiseFulfilled) {
                    // When catching the error, we don't know exactly whether it's a cache miss because the error message "Failed to fetch" gets localized by browser
                    promiseFulfilled = true;
                    navigator.serviceWorker.getRegistration(OfficePwa.BasePwaPath)
                        .then(function (registration) { return registration.unregister(); })
                        .then(function (success) {
                        if (success) {
                            _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'CacheMissUnregister', 0 /* Security */);
                            _this_1.reloadApp();
                        }
                        else {
                            throw new Error('unregister failed');
                        }
                    }).catch(function (err) {
                        var _a;
                        _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'CacheMissHandling_Error', 0 /* Security */, (_a = {},
                            _a[OfficePwa.InstrumentationParamError] = err && err.message ? err.message.toString() : '',
                            _a));
                        // Default to original error handling behavior
                        handleError();
                    });
                }
                else {
                    handleError();
                }
            });
        }
    };
    OfficePwa.prototype.webAccountProviderCommandInvoked = function (providerCommand) {
        var _this_1 = this;
        if (!providerCommand || !providerCommand.webAccountProvider || !providerCommand.webAccountProvider.authority) {
            this.logAriaEvent(LoggingUtil.GenericEvent, 'InvalidWamProvider_Error', 2 /* Enhanced */);
            return;
        }
        var provider = providerCommand.webAccountProvider;
        if (this.localSettingsProps.hasKey(OfficePwa.SwitchAccountKey)) {
            this.localSettingsProps.insert(OfficePwa.SwitchToSelectedAccountKey, true);
            var currentProvider_1 = this.currentAccountProvider;
            this.saveAccountProvider(provider);
            var pickedProviderTime_1 = LoggingUtil.CurrentTime();
            var fetchedTokenTime_1;
            // if switching account, we redirect user back to /pwa.
            this.getOfficeToken(provider, false).then(function (_tokenResult) {
                fetchedTokenTime_1 = LoggingUtil.CurrentTime();
                _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'SwitchAccount_FetchedToken', 2 /* Enhanced */, { Latency: Math.ceil(fetchedTokenTime_1 - pickedProviderTime_1).toString() });
                // Ignore token result here since it will be cached for next page load
                _this_1.localSettingsProps.remove(OfficePwa.SwitchAccountKey);
                _this_1.localSettingsProps.remove(OfficePwa.SwitchToSelectedAccountKey);
                _this_1.resetBrowserStorage();
                return _this_1.forceNewServiceWorker();
            }).then(function () {
                _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'SwitchAccount_Completed', 2 /* Enhanced */, { Latency: Math.ceil(LoggingUtil.CurrentTime() - fetchedTokenTime_1).toString() });
                _this_1.reloadApp();
            }).catch(function () {
                // Clear switch account if we fail to get token or new worker
                _this_1.localSettingsProps.remove(OfficePwa.SwitchAccountKey);
                _this_1.localSettingsProps.remove(OfficePwa.SwitchToSelectedAccountKey);
                _this_1.saveAccountProvider(currentProvider_1);
                _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'SwitchAccount_Error', 2 /* Enhanced */, { Latency: Math.ceil(LoggingUtil.CurrentTime() - pickedProviderTime_1).toString() });
                _this_1.displayScreen(OfficePwa.OfficeHomeId);
            });
        }
        else {
            this.saveAccountProvider(provider);
            this.displayScreen(OfficePwa.OfficeHomeId);
            this.getOfficeToken(provider).then(function (tokenResult) {
                _this_1.bootstrapApp(_this_1.isConsumerAuthority(provider.authority), tokenResult.responseData[0].token);
            }).catch(function (err) {
                // If user picks a new provider from WAM and didn't complete sign in dialog, userCancel is returned as response status
                // In that case, throw a specific error so it can be handled later
                if (err && err.responseStatus && err.responseStatus === Windows.Security.Authentication.Web.Core.WebTokenRequestStatus.userCancel) {
                    _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'UserCancelSignIn', 2 /* Enhanced */);
                    _this_1.displayScreen(OfficePwa.UnauthPageId);
                }
                else {
                    _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'GetOfficeToken_Error', 2 /* Enhanced */);
                    _this_1.showErrorPage();
                }
                _this_1.clearSavedProvider();
            });
        }
    };
    OfficePwa.prototype.onAccountCommandsRequested = function (args) {
        var _this_1 = this;
        var deferral = args.getDeferral();
        var authorityPromises = new Array();
        if (this.requiredMockAuthority) {
            authorityPromises.push(new Promise(function (resolve) { return Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.findAccountProviderAsync(OfficePwa.MsaAadProviderId, _this_1.requiredMockAuthority).then(resolve); }));
        }
        else {
            // We need to wrap findAccountProviderAsync in a new Promise object because Promise.all doesn't work correctly with the APIs IAsyncOperation return type
            authorityPromises.push(new Promise(function (resolve) { return Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.findAccountProviderAsync(OfficePwa.MsaAadProviderId, OfficePwa.AadAuthority).then(resolve); }));
            authorityPromises.push(new Promise(function (resolve) { return Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.findAccountProviderAsync(OfficePwa.MsaAadProviderId, OfficePwa.MsaAuthority).then(resolve); }));
        }
        Promise.all(authorityPromises).then(function (providers) {
            providers.forEach(function (provider) {
                var providerCommand = new Windows.UI.ApplicationSettings.WebAccountProviderCommand(provider, function (command) { return _this_1.webAccountProviderCommandInvoked(command); });
                args.webAccountProviderCommands.push(providerCommand);
            });
            deferral.complete();
        });
    };
    OfficePwa.prototype.showAccountsPane = function (isSwitchAccount) {
        var _this_1 = this;
        if (isSwitchAccount === void 0) { isSwitchAccount = false; }
        if (!this.initializedAccountsPane) {
            Windows.UI.ApplicationSettings.AccountsSettingsPane.getForCurrentView().onaccountcommandsrequested = function (args) { return _this_1.onAccountCommandsRequested(args); };
            this.initializedAccountsPane = true;
        }
        var accountSettingsPane = Windows.UI.ApplicationSettings.AccountsSettingsPane;
        if (isSwitchAccount) {
            if (accountSettingsPane.showManageAccountsAsync) {
                accountSettingsPane.showManageAccountsAsync().then(function () { return _this_1.localSettingsProps.remove(OfficePwa.SwitchAccountKey); });
            }
            else {
                this.logAriaEvent(LoggingUtil.GenericEvent, 'MissingAccountSettingsPaneApi', 2 /* Enhanced */);
                accountSettingsPane.show();
            }
        }
        else {
            accountSettingsPane.show();
        }
    };
    OfficePwa.prototype.reloadApp = function () {
        var _this_1 = this;
        this.flushLogs(true).then(function () {
            // Delaying visibility until view is ready
            // Reference: https://docs.microsoft.com/en-us/microsoft-edge/windows-runtime/reference/msapp#delaying-visibility
            window.open("" + OfficePwa.BasePwaPath + _this_1.currentUrl.search, '_self', 'msHideView=yes', true);
        });
    };
    /**
     * Bootstraps the app, including loading Shell and App bundles. There are 2 entry points for this:
     * - User auto signs in (previously signed in so we cached the information, or user is logged in to Windows with Live ID/AAD)
     * - User goes to unauth page and picks an account from WAM
     * @param isConsumer Whether user is consumer (MSA)
     * @param authToken Token for fetching shell/config API. Only passed in during bootstrapping from unauth page
     */
    OfficePwa.prototype.bootstrapApp = function (isConsumer, authToken) {
        var _this_1 = this;
        LoggingUtil.RevokeStandaloneLogger();
        this.logAriaEvent(LoggingUtil.GenericEvent, 'Pwa_Bootstrap_Start', 0 /* Security */);
        // Since app bootstrap can start much later in unauth sign in scenario, we mark separate initial markers that would look like the following:
        // (Auto sign in) LastAppNavigationStart --> Pwa_Init --> Auth_Pwa_Bootstrap_Start
        // (Unauth) LastAppNavigationStart --> Pwa_Init --> Pwa_Unauth_PageView ... [user signs in] ... Unauth_Pwa_Bootstrap_Start
        var initialPerfEvent = authToken ? undefined : OfficePwa.InitialPerformanceEvent;
        var perfMarkerName = (authToken ? 'Unauth' : 'Auth') + "_Pwa_Bootstrap_Start";
        window.measureAppFrom = perfMarkerName;
        this.markPerformance(perfMarkerName, 0 /* Security */, initialPerfEvent);
        this.isConsumer = isConsumer;
        if (window.pwaCurrentUserId) {
            BootstrapUtil.InitDiskPreload(window.pwaCurrentUserId, window.diskPreloadRequestMap, OfficePwa.DiskPreloadStorageKey);
        }
        var _this = this;
        window.shellAndConfigResponse = new Promise(function (resolve, reject) {
            // Save the resolve/reject callbacks in local variables so in case we need to show account picker, we can call them in that async flow
            _this.shellAndConfigPromiseResolver = resolve;
            _this.shellAndConfigPromiseRejecter = reject;
            if (!_this.isUwp) {
                window.pwaCurrentUserId = 'mock user';
                _this.fetchAndHandleShellAndConfigResponse(isConsumer, 'mock token');
                return;
            }
            _this.getAccountProvider().then(function (accountProvider) {
                if (authToken) {
                    _this.fetchAndHandleShellAndConfigResponse(isConsumer, authToken);
                }
                else {
                    _this.fetchTokenAndCallShellAndConfigApi(accountProvider);
                }
            }).catch(function () {
                _this.shellAndConfigPromiseRejecter('fetch provider error');
                _this.cleanUpShellAndConfigCallbacks();
            });
        });
        window.shellAndConfigResponse.then(function () {
            // Once user is able to fetch Shell/config, it should be safe to assume they are fully signed in and will see the app being rendered.
            // Once this completes, we want to clear the UserSignedOut key. This key exists if:
            // 1. User is signing in fresh
            // 2. User had previously signed in, but we could not fetch Office.com token using the cached IDs, so we force them back to unauth page
            //    to sign in again
            if (_this_1.localSettingsProps && _this_1.localSettingsProps.hasKey(OfficePwa.SignedOutKey)) {
                _this_1.localSettingsProps.remove(OfficePwa.SignedOutKey);
            }
        }, function () {
            _this.showErrorPage();
        });
        return this.waitForDocumentLoad().then(function () {
            var config = _this_1.getPwaStartupConfig();
            config.isConsumer = isConsumer;
            var shellResourceUrl = config.shellBootHost + (isConsumer ? config.consumerBootstrapPath : config.commercialBootstrapPath);
            var appIconsJSUrl = isConsumer ? config.consumerAppIconsUrl : config.commercialAppIconsUrl;
            var appIconResourceURLfromCDN = config.shellBootHost + appIconsJSUrl;
            var shellScriptNode = document.createElement('script');
            var appScriptNode = document.createElement('script');
            var appIconsScriptNode = document.createElement('script');
            appScriptNode.async = shellScriptNode.async = appIconsScriptNode.async = true;
            appScriptNode.crossOrigin = shellScriptNode.crossOrigin = appIconsScriptNode.crossOrigin = 'anonymous';
            appScriptNode.type = shellScriptNode.type = appIconsScriptNode.type = 'text/javascript';
            appScriptNode.onload = function () {
                _this_1.markPerformance('Pwa_App_Bundle_Load', 0 /* Security */, perfMarkerName);
            };
            appScriptNode.onerror = function () {
                var _a;
                var params = (_a = {},
                    _a[OfficePwa.InstrumentationParamAction] = window.bundleUrl,
                    _a);
                _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'App_Bundle_Load_Error', 0 /* Security */, params);
            };
            shellScriptNode.onload = function () {
                _this_1.markPerformance('Pwa_Shell_Bootstrapper_Load', 0 /* Security */, perfMarkerName);
                _this_1.shellShimReady = true;
                if (_this_1.configRetrieved && !_this_1.reactShellBootstrapped) {
                    _this_1.bootstrapReactShell();
                }
            };
            shellScriptNode.onerror = function () {
                var _a;
                var params = (_a = {},
                    _a[OfficePwa.InstrumentationParamAction] = shellResourceUrl,
                    _a);
                _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'Shell_Bootstrapper_Load_Error', 0 /* Security */, params);
            };
            appIconsScriptNode.onload = function () {
                _this_1.markPerformance('Pwa_Shell_AppIcons_Load', 0 /* Security */);
            };
            appIconsScriptNode.onerror = function () {
                var _a;
                var params = (_a = {},
                    _a[OfficePwa.InstrumentationParamAction] = appIconResourceURLfromCDN,
                    _a);
                _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'Pwa_Shell_AppIcons_Error', 0 /* Security */, params);
            };
            shellScriptNode.src = shellResourceUrl;
            appScriptNode.src = window.bundleUrl;
            appIconsScriptNode.src = appIconResourceURLfromCDN;
            if (window.bundleIntegrity) {
                appScriptNode.integrity = window.bundleIntegrity;
            }
            document.body.appendChild(appScriptNode);
            document.body.appendChild(shellScriptNode);
            document.body.appendChild(appIconsScriptNode);
        });
    };
    OfficePwa.prototype.bootstrapReactShell = function () {
        var _a;
        try {
            if (this.isConsumer) {
                this.reactShellConfig.isConsumer = true;
                if (this.currentMsaUser) {
                    this.reactShellConfig.upn = this.currentMsaUser.upn;
                    this.reactShellConfig.puid = this.currentMsaUser.puid;
                    this.reactShellConfig.userDisplayName = this.currentMsaUser.userDisplayName;
                    this.reactShellConfig.mePhotoSource = !!this.currentUserPhoto ? 'data:image/jpeg;base64,' + this.currentUserPhoto : '';
                }
            }
            if (!HomePage.ShellUtil.BootstrapReactShell('OfficePwa', this.reactShellConfig, this.capabilitiesConfig)) {
                throw new Error('Bootstrap failed');
            }
            else {
                this.reactShellBootstrapped = true;
                if (!this.isConsumer) {
                    HomePage.ShellUtil.HydrateCommercialReactShell(window.navBarData, true, this.reactShellConfig, this.capabilitiesConfig);
                }
            }
        }
        catch (error) {
            var errorMsg = typeof error === 'string' ? error : error.name + ": " + error.message;
            var params = (_a = {},
                _a[OfficePwa.InstrumentationParamError] = errorMsg,
                _a);
            this.logAriaEvent(LoggingUtil.GenericEvent, 'Shell_Bootstrapper_Error', 0 /* Security */, params);
        }
    };
    OfficePwa.prototype.resetBrowserStorage = function () {
        sessionStorage.clear();
        sessionStorage.setItem(OfficePwa.ExistingSessionKey, 'true');
        localStorage.removeItem(OfficePwa.PwaOnActivatePendingEventArgsKey);
        this.clearPwaJumpList();
    };
    OfficePwa.prototype.resetSecurityLevel = function () {
        var level = Windows.System.Profile.PlatformDiagnosticsAndUsageDataSettings.collectionLevel;
        if (typeof (level) !== 'number') {
            // Default to no logging when OS setting is unexpected value
            window.windowsDataSecuritySetting = 0 /* Security */;
            return;
        }
        if (level >= 3 /* Full */) {
            window.windowsDataSecuritySetting = 3 /* Full */;
            return;
        }
        window.windowsDataSecuritySetting = level;
    };
    // TODO: Re-evaluate usage of lifecycle hooks with/without prelaunch.
    // It seems like we won't capture activations from termination state or navigations due to redirect and absence of logger
    // If this is the case then we should find a different way to capture activations from terminated state
    // Also remove unnecessary event properties
    OfficePwa.prototype.onLifecycleEvent = function (eventName, event, params) {
        params = params || {};
        params['Type'] = event.type;
        params['Target'] = event.target ? event.target.toString() : '';
        this.logAriaEvent(LoggingUtil.GenericEvent, "UWPLifecycleEvent_" + eventName, 2 /* Enhanced */, params);
    };
    OfficePwa.prototype.onActivate = function (eventData) {
        var _a, _b, _c, _d;
        var details = eventData.detail && eventData.detail.length ? eventData.detail[0] : null;
        var launchArguments = '';
        if (eventData.kind === Windows.ApplicationModel.Activation.ActivationKind.protocol) {
            var protocolLaunchEventData = eventData;
            launchArguments = (_a = protocolLaunchEventData === null || protocolLaunchEventData === void 0 ? void 0 : protocolLaunchEventData.uri.absoluteUri) !== null && _a !== void 0 ? _a : '';
        }
        else {
            launchArguments = (_b = details === null || details === void 0 ? void 0 : details.arguments) !== null && _b !== void 0 ? _b : '';
        }
        var launchArgumentsFormatted = launchArguments.toLowerCase();
        var fromOfficeKey = launchArgumentsFormatted.includes('officekey');
        var fromWordPad = launchArgumentsFormatted.includes('wordpad');
        var fromCovid = launchArgumentsFormatted.includes('covid');
        var fromHva = launchArgumentsFormatted.includes('hvastartmenu');
        var fromAppId = launchArgumentsFormatted.includes('appid');
        var fromActionCenter = launchArgumentsFormatted.includes('cgeawareness');
        /*
            Possible WordPad arguments:
                1. from=WordPadVariant1A
                2. from=WordPadVariant1B
                3. from=WordPadVariant1C
                4. from=WordPadVariant2A&open=WordStartPage
                5. from=WordPadVariant2B&open=WordStartPage
                6. from=WordPadVariant2C&open=WordStartPage
            Possible Covid arguments:
                1. from=CovidLockscreen&open=ms-officeapp
                2. from=CovidLockscreen&open=WordStartPage
                3. from=CovidLockscreen&open=ExcelStartPage
                4. from=CovidStartMenu&open=ms-officeapp
                5. from=CovidStartMenu&open=WordStartPage
                6. from=CovidStartMenu &open=ExcelStartPage
                7. from=CovidAMC&open=ms-officeapp
                8. from=CovidAMC&open=WordStartPage
                9. from=CovidAMC&open=ExcelStartPage
                10. from=CovidActionCenter&open=ms-officeapp
            Possible HVA arguments:
                1. from=HVAStartMenu&open=ms-officeapp
                2. from=HVAStartMenu&open=WordStartPage
                3. from=HVAStartMenu&open=ExcelStartPage
                4. from=HVAStartMenu&open=PowerPointStartPage
            Possible Office Url Segment arguments:
                1. appId={appId}&pageId={pageId}&subpageId={subpageId}
            Possible Action Center in product campaign arguments:
                2. from=CGEAwareness&open=WordStartPage
                3. from=CGEAwareness&open=ExcelStartPage
                4. from=CGEAwareness&open=PowerPointStartPage
        */
        if (fromOfficeKey) {
            window.requestOriginOverride = 'OfficeKey';
        }
        else if (launchArgumentsFormatted && (fromWordPad || fromCovid || fromHva || fromAppId || fromActionCenter)) {
            var urlSearchParams = new URLSearchParams();
            var origin_1 = '';
            if (fromAppId) {
                // Support launching MOS app
                // launchArgumentFormatted looks as: "ms-getoffice:'appid=07b75f22-72b5-4063-b7fc-0ed5ea8ff3ff&pageid=milestones&subpageid=m16'"
                var launchArgumentsKeyValue = launchArgumentsFormatted.split(':');
                urlSearchParams =
                    launchArgumentsKeyValue.length > 1
                        ? new URLSearchParams(launchArgumentsKeyValue[1].split("'")[1])
                        : new URLSearchParams(launchArgumentsFormatted);
                origin_1 = 'M365Apps';
            }
            else {
                // Support deeplinking to sub pages.
                // For UWP, launchArgumentFormatted looks as: "ms-officeapp:'wwahost?open=wordstartpage&from=wordpad'"
                // For HWA, launchArgumentFormatted looks as: "ms-getoffice:'page:https://www.office.com/pwa?open=wordstartpage&from=wordpad'"
                var searchParams = (_c = launchArguments.split('?')[1]) === null || _c === void 0 ? void 0 : _c.replace("'", '');
                urlSearchParams = new URLSearchParams(searchParams);
                origin_1 = (_d = urlSearchParams.get('from')) !== null && _d !== void 0 ? _d : '';
            }
            var appId = urlSearchParams.get('appid');
            var pageId = urlSearchParams.get('pageid');
            var subpageId = urlSearchParams.get('subpageid');
            window.requestOriginOverride = origin_1;
            // Save to local storage and dispatch event
            var openStartPage = 'Default';
            if (launchArgumentsFormatted.includes('open=wordstartpage')) {
                openStartPage = 'Word';
            }
            else if (launchArgumentsFormatted.includes('open=excelstartpage')) {
                openStartPage = 'Excel';
            }
            else if (launchArgumentsFormatted.includes('open=powerpointstartpage')) {
                openStartPage = 'PowerPoint';
            }
            else if (fromAppId) {
                openStartPage = '';
                if (appId) {
                    openStartPage += '/' + appId;
                }
                if (pageId) {
                    openStartPage += '/' + pageId;
                }
                if (subpageId) {
                    openStartPage += '/' + subpageId;
                }
            }
            var pendingEvent = { by: origin_1, argument: openStartPage };
            window.localStorage.setItem(OfficePwa.PwaOnActivatePendingEventArgsKey, JSON.stringify(pendingEvent));
            window.dispatchEvent(new CustomEvent('activated', { detail: pendingEvent }));
            this.logAriaEvent(LoggingUtil.GenericEvent, OfficePwa.InstrumentationEventOnActivate, 2 /* Enhanced */);
        }
        if (details.kind === Windows.ApplicationModel.Activation.ActivationKind.launch) {
            this.localSettingsProps['SplashImageWidth'] = details.splashScreen.imageLocation.width;
            // Handle activation from pwa jump list
            var launchEventData = eventData;
            if (launchEventData.arguments && !fromOfficeKey && !fromWordPad && !fromActionCenter) {
                try {
                    var args = JSON.parse(launchEventData.arguments);
                    if (args.createFileType || args.id) {
                        var jumpListEventParams = {};
                        jumpListEventParams[OfficePwa.InstrumentationParamState] = 'Pending';
                        jumpListEventParams[OfficePwa.InstrumentationParamAction] = args.createFileType ? 'CreateNewFile' : 'OpenFile';
                        // Save to local storage and dispatch event
                        var pendingEvent = { by: 'JumpList', argument: launchEventData.arguments, previousExecutionState: typeof details.previousExecutionState !== 'undefined' ? details.previousExecutionState : 0 };
                        window.localStorage.setItem(OfficePwa.PwaOnActivatePendingEventArgsKey, JSON.stringify(pendingEvent));
                        window.dispatchEvent(new CustomEvent('activated', { detail: pendingEvent }));
                        window.requestOriginOverride = 'JumpList';
                        this.logAriaEvent(LoggingUtil.GenericEvent, OfficePwa.InstrumentationEventJumpListItemClick, 2 /* Enhanced */, jumpListEventParams);
                    }
                }
                catch (error) {
                    var errorParam = {};
                    errorParam[OfficePwa.InstrumentationParamState] = 'Pending';
                    errorParam[OfficePwa.InstrumentationParamError] = (error || '').toString();
                    errorParam[OfficePwa.InstrumentationParamAction] = 'ActivateByJumpList';
                    this.logAriaEvent(LoggingUtil.GenericEvent, OfficePwa.InstrumentationEventJumpList, 2 /* Enhanced */, errorParam);
                }
            }
        }
        if (details.prelaunchActivated) {
            this.setPrelaunchMode();
            this.localSettingsProps[OfficePwa.PrelaunchModeKey] = true;
        }
        var params = details ? {
            Kind: Windows.ApplicationModel.Activation.ActivationKind[details.kind],
            PreviousState: Windows.ApplicationModel.Activation.ApplicationExecutionState[details.previousExecutionState],
            TileId: details.tileId,
            ViewId: details.currentlyShownApplicationViewId.toString(),
            FromOfficeKey: fromOfficeKey
        } : {};
        this.onLifecycleEvent('Activate', eventData, params);
        if (!details.prelaunchActivated &&
            Windows.UI.WebUI.WebUIApplication.hasOwnProperty('enablePrelaunch') &&
            typeof (Windows.UI.WebUI.WebUIApplication.enablePrelaunch) === 'function') {
            Windows.UI.WebUI.WebUIApplication.enablePrelaunch(true);
        }
    };
    OfficePwa.prototype.onSuspend = function (eventData) {
        // TODO: Save state (such as current page) to be used in case of termination (and figure out how to capture activations after termination)
        // TODO: Ensure Aria has time to write this log if online
        this.onLifecycleEvent('Suspend', eventData);
    };
    OfficePwa.prototype.onResume = function (eventData) {
        window.pwaResuming = true;
        this.onLifecycleEvent('Resume', eventData);
    };
    OfficePwa.prototype.onNavigate = function (eventData) {
        this.onLifecycleEvent('Navigate', eventData);
    };
    OfficePwa.prototype.onEnterBackground = function (eventData) {
        this.onLifecycleEvent('EnterBackground', eventData);
    };
    OfficePwa.prototype.onLeaveBackground = function (eventData) {
        this.onLifecycleEvent('LeaveBackground', eventData);
    };
    OfficePwa.prototype.isConsumerAuthority = function (authority) {
        return authority === OfficePwa.MsaAuthority;
    };
    OfficePwa.prototype.installThirdPartyNotice = function () {
        var _this_1 = this;
        var localFolder = Windows.Storage.ApplicationData.current.localFolder;
        fetch(window.thirdPartyNoticeUrl).then(function (response) {
            if (!response.ok) {
                throw new Error();
            }
            return response.arrayBuffer().then(function (buffer) {
                var byteArray = Windows.Security.Cryptography.CryptographicBuffer.createFromByteArray((Array.from(new Uint8Array(buffer))));
                return localFolder.createFileAsync(OfficePwa.ThirdPartyNoticeFileName, Windows.Storage.CreationCollisionOption.replaceExisting).then(function (file) {
                    _this_1.thirdPartyLicenseNotice = file;
                    return Windows.Storage.FileIO.writeBufferAsync(file, byteArray);
                });
            });
        }).catch(function () {
            _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'InstallThirdPartyNotice_Error', 2 /* Enhanced */);
            return localFolder.getFileAsync(OfficePwa.ThirdPartyNoticeFileName).then(function (file) {
                _this_1.thirdPartyLicenseNotice = file;
            });
        }).catch(function () {
            _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'GetThirdPartyNotice_Error', 2 /* Enhanced */);
        });
    };
    OfficePwa.prototype.forceNewServiceWorker = function () {
        var _this_1 = this;
        if (!this.swEnabled) {
            return Promise.resolve();
        }
        return new Promise(function (resolve) {
            // When serviceWorker.getRegistration() is called without explicitly specifying the clientUrl/scope, it takes the value from
            // document.baseURI. Having a <base href='' /> tag changes this value. If the current service worker has a scope that is not
            // set to root, calling getRegistration() will not return anything
            navigator.serviceWorker.getRegistration(OfficePwa.BasePwaPath).then(function (registration) {
                if (registration) {
                    var pendingWorker = registration.waiting;
                    if (pendingWorker) {
                        // If we have a waiting worker, do postMessage to force activation and wait until activation or timeout
                        var controllerChanged_1 = false;
                        var controllerChangePromise = new Promise(function (resolve2) {
                            navigator.serviceWorker.oncontrollerchange = function () {
                                controllerChanged_1 = true;
                                resolve2();
                            };
                        });
                        var timeoutPromise = new Promise(function (resolve2) {
                            setTimeout(resolve2, 500);
                        });
                        pendingWorker.postMessage({
                            type: 'OFFICECOM_FORCE_ACTIVATION'
                        });
                        Promise.race([controllerChangePromise, timeoutPromise]).then(function () {
                            if (!controllerChanged_1) {
                                _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'SWForceActivation_Error', 0 /* Security */);
                            }
                            resolve();
                        });
                        return;
                    }
                    else if (registration.active) {
                        var unregisterServiceWorker_1 = function () { return registration.unregister().then(function (_) { return resolve(); }).catch(function (_) { return resolve(); }); };
                        caches.keys().then(function (cacheKeys) {
                            if (cacheKeys) {
                                var cacheExists = cacheKeys.some(function (cacheKey) { return cacheKey.indexOf(window.pwaCurrentUserId) >= 0 && cacheKey.indexOf('shell-config-api') >= 0; });
                                if (cacheExists) {
                                    resolve();
                                    return;
                                }
                            }
                            unregisterServiceWorker_1();
                        }, unregisterServiceWorker_1);
                        return;
                    }
                }
                resolve();
            });
        });
    };
    /**
     * Utility method to show a screen
    */
    OfficePwa.prototype.displayScreen = function () {
        var elementIds = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            elementIds[_i] = arguments[_i];
        }
        if (elementIds && elementIds.length) {
            if (elementIds.includes(OfficePwa.OfficeHomeId)) {
                document.getElementById(OfficePwa.ReactShellHeaderId).style.display = 'block';
            }
            this.elementsDisplayStyleMap.forEach(function (value, key) {
                var element = document.getElementById(key);
                if (element) {
                    if (elementIds.includes(key)) {
                        element.style.display = value;
                    }
                    else {
                        element.style.display = 'none';
                    }
                }
            });
        }
    };
    OfficePwa.prototype.showVersionUpdatePage = function () {
        var _this_1 = this;
        this.logAriaEvent(LoggingUtil.GenericEvent, 'Pwa_Version_Update_PageView', 0 /* Security */);
        this.waitForPageLoad().then(function () {
            _this_1.displayScreen(OfficePwa.VersionUpdatePageId);
            _this_1.markPerformance('Pwa_Version_Update_Ready', 0 /* Security */, OfficePwa.InitialPerformanceEvent);
            _this_1.flushLogs();
        });
    };
    OfficePwa.prototype.showErrorPage = function () {
        var _this_1 = this;
        var showPage = function () {
            _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'Pwa_Error_PageView', 0 /* Security */);
            _this_1.waitForPageLoad().then(function () {
                _this_1.flushLogs();
                document.getElementById('correlation-id').innerText = window.overrideCID;
                _this_1.displayScreen(OfficePwa.ErrorPageId);
                _this_1.markPerformance('Pwa_Error_Ready', 0 /* Security */);
            });
        };
        if (!this.swEnabled) {
            showPage();
            return;
        }
        // Tell active worker to unregister if there was an issue loading the cached page
        navigator.serviceWorker.getRegistration(OfficePwa.BasePwaPath).then(function (registration) {
            if (!registration || !registration.active) {
                showPage();
                return Promise.resolve(false);
            }
            _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'Pwa_FoundErrorWithSW', 0 /* Security */);
            // Fall into catch block on failure to unregister
            return registration.unregister().then(function (success) {
                if (!success) {
                    return Promise.reject();
                }
                return Promise.resolve(success);
            });
        }).then(function (unregistered) {
            if (!unregistered) {
                return;
            }
            _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'Pwa_WorkerUnregistration_Success', 0 /* Security */);
            _this_1.reloadApp();
        }).catch(function () {
            _this_1.logAriaEvent(LoggingUtil.GenericEvent, 'Pwa_WorkerUnregistration_Failed', 0 /* Security */);
            showPage();
        });
    };
    /**
     * Enables prelaunch mode and clears it when window receives focus (user starts interaction with app)
     */
    OfficePwa.prototype.setPrelaunchMode = function () {
        var _this_1 = this;
        window.prelaunchMode = true;
        if (Math.random() > OfficePwa.PrelaunchLogsSamplingRate) {
            window.disableLogs = true;
        }
        var _this = this;
        var clearPrelaunchMode = function () {
            // setPrelaunchMode can be called more than once, so ensure we log the window focus event only once
            if (window.prelaunchMode) {
                window.prelaunchMode = false;
                window.disableLogs = false;
                _this.localSettingsProps.remove(OfficePwa.PrelaunchModeKey);
                // log event after window.disableLogs set to false so the event doesn't get sampled
                _this.logAriaEvent(LoggingUtil.GenericEvent, 'Pwa_FocusWindow_PageView', 0 /* Security */);
                if (_this_1.currentAccountProvider) {
                    _this_1.initializeAuthOTelAndLogPageView({ pageName: 'OfficeHome', pageType: 'Default', eventName: 'Pwa_FocusWindow_PageView', isIntentional: true, securityThreshold: 0 /* Security */ });
                }
            }
            window.removeEventListener('focus', clearPrelaunchMode);
        };
        window.addEventListener('focus', clearPrelaunchMode);
    };
    OfficePwa.prototype.getAriaContext = function () {
        var _this_1 = this;
        return this.getSharedClientStartupConfig().then(function (config) {
            if (!config) {
                return Promise.reject('Startup config not found');
            }
            return Promise.resolve({
                appWorkloadType: _this_1.getAppWorkloadType(),
                ariaCollectorUrl: config.ariaCollectorUrl,
                ariaTenant: config.ariaTenant,
                authTypeString: _this_1.currentAccountProvider ? _this_1.currentAccountProvider.authority : '',
                authVersion: '1.0',
                cookieConsentRequired: config.cookieConsentRequired,
                corpNet: config.corpNet,
                correlationId: window.overrideCID,
                deploymentEnvironment: config.deploymentEnvironment,
                devEnvironment: config.devEnvironment,
                ecsRing: config.ecsRing,
                features: '',
                flights: config.flights,
                geoName: config.geoName,
                domainOrigin: config.domainOrigin,
                puid: '',
                requestOrigin: window.requestOriginOverride || config.requestOrigin,
                serverBuildDate: config.buildDateUtc,
                serverBuildId: config.buildId,
                sessionId: window.overrideSID,
                tenantId: '',
                testTraffic: config.testTraffic,
                wrapperVersion: _this_1.getVersionString(),
                workload: 'Pwa'
            });
        });
    };
    OfficePwa.prototype.initializeUnAuthOTelAndLogPageView = function (property) {
        this.getSharedClientStartupConfig().then(function (config) {
            if (!config) {
                return Promise.reject('Startup config not found');
            }
            return Promise.resolve({
                appName: config.oTelAppName,
                appPlatform: 'Universal',
                appWorkload: 'HWA',
                bundleBuildDate: config.buildDateUtc,
                bundleBuildId: config.buildId,
                correlationId: config.correlationId,
                deploymentEnvironment: config.deploymentEnvironment,
                eventCategoryConfig: {
                    actionEnabled: config.oTelClickEnabled,
                    diagnosticEnabled: config.oTelDiagnosticEnabled,
                    errorEnabled: config.oTelErrorEnabled,
                    impressionEnabled: config.oTelImpressionEnabled,
                    pageViewEnabled: config.oTelPageViewEnabled,
                    perfEnabled: config.oTelPerfEnabled,
                    requestEnabled: config.oTelRequestEnabled,
                    featureEnabled: config.oTelFeatureEnabled
                },
                flights: config.flights,
                isCorpNet: config.corpNet,
                isTestTraffic: config.testTraffic,
                oTelEnabled: config.oTelEnabled,
                oTelAriaTenant: config.oTelAriaTenant,
                oTelAriaNameSpace: config.oTelNameSpace,
                sessionId: config.sessionId,
                serverLocation: config.geoName,
                requestOrigin: config.requestOrigin
            });
        }).then(function (context) {
            var _a, _b;
            (_a = window.standaloneOteLogger) === null || _a === void 0 ? void 0 : _a.initializeUnAuthOTel(context);
            (_b = window.standaloneOteLogger) === null || _b === void 0 ? void 0 : _b.logPageView(property, true);
        }).catch(function (error) {
            console.log(error);
        });
    };
    OfficePwa.prototype.initializeAuthOTelAndLogPageView = function (property) {
        var _this_1 = this;
        this.getSharedClientStartupConfig().then(function (config) {
            if (!config) {
                return Promise.reject('Startup config not found');
            }
            return Promise.resolve({
                appName: config.oTelAppName,
                appPlatform: 'Universal',
                appWorkload: 'HWA',
                bundleBuildDate: config.buildDateUtc,
                bundleBuildId: config.buildId,
                correlationId: config.correlationId,
                deploymentEnvironment: config.deploymentEnvironment,
                eventCategoryConfig: {
                    actionEnabled: config.oTelClickEnabled,
                    diagnosticEnabled: config.oTelDiagnosticEnabled,
                    errorEnabled: config.oTelErrorEnabled,
                    impressionEnabled: config.oTelImpressionEnabled,
                    pageViewEnabled: config.oTelPageViewEnabled,
                    perfEnabled: config.oTelPerfEnabled,
                    requestEnabled: config.oTelRequestEnabled,
                    featureEnabled: config.oTelFeatureEnabled
                },
                flights: config.flights,
                identity: {
                    isMsa: _this_1.currentAccountProvider ? _this_1.isConsumerAuthority(_this_1.currentAccountProvider.authority) : undefined,
                    objectId: config.objectId,
                    puid: config.puid,
                    tenantId: config.tenantId,
                    encryptionConfig: undefined,
                    userPrincipalName: undefined
                },
                isCorpNet: config.corpNet,
                isTestTraffic: config.testTraffic,
                oTelEnabled: config.oTelEnabled,
                oTelAriaTenant: config.oTelAriaTenant,
                oTelAriaNameSpace: config.oTelNameSpace,
                sessionId: config.sessionId,
                serverLocation: config.geoName,
                requestOrigin: config.requestOrigin
            });
        }).then(function (context) {
            var _a, _b;
            (_a = window.standaloneOteLogger) === null || _a === void 0 ? void 0 : _a.initializeAuthOTel(context);
            (_b = window.standaloneOteLogger) === null || _b === void 0 ? void 0 : _b.logPageView(property, false);
        }).catch(function (error) {
            console.log(error);
            var errorParams = {
                Error: typeof error === 'string' ? error : error ? error.name : '',
                DebugInfo: error ? error.message : ''
            };
            _this_1.logAriaEvent(LoggingUtil.GenericDebugCache, 'Pwa_Initialize_OTEL_Error', 0 /* Security */, errorParams);
        });
    };
    OfficePwa.prototype.logUnAuthUserAction = function (property) {
        var _a;
        // TODO: Remove the Aria logging once OTEL migration verification done
        this.logAriaEvent(LoggingUtil.GenericClick, property.id, property.securityThreshold);
        (_a = window.standaloneOteLogger) === null || _a === void 0 ? void 0 : _a.logUnAuthUserAction(property);
    };
    OfficePwa.prototype.getVersionString = function () {
        var version = this.isUwp ? Windows.ApplicationModel.Package.current.id.version : null;
        return version && version.major + "." + version.minor + "." + version.build + "." + version.revision;
    };
    OfficePwa.prototype.flushLogs = function (teardown) {
        if (teardown === void 0) { teardown = false; }
        return LoggingUtil.FlushQueuedEvents(this.getAriaContext(), teardown, OfficePwa.PwaPage);
    };
    OfficePwa.prototype.parseJsonElementWithId = function (elementId) {
        var jsonElement = document.getElementById(elementId);
        if (jsonElement && jsonElement.textContent) {
            return JSON.parse(jsonElement.textContent);
        }
        else {
            return null;
        }
    };
    OfficePwa.prototype.getSharedClientStartupConfig = function () {
        var _this_1 = this;
        return this.waitForDocumentLoad().then(function () { return _this_1.parseJsonElementWithId('sharedClientStartupConfigJson'); });
    };
    OfficePwa.prototype.getPwaStartupConfig = function () {
        return this.parseJsonElementWithId(OfficePwa.PwaStartupConfigElementId);
    };
    // Waits for window onload event
    OfficePwa.prototype.waitForPageLoad = function () {
        return new Promise(function (resolve) {
            if (document.readyState === 'complete') {
                resolve();
            }
            else {
                window.addEventListener('load', function () {
                    resolve();
                });
            }
        });
    };
    // Waits for DOMContentLoaded event
    OfficePwa.prototype.waitForDocumentLoad = function () {
        return new Promise(function (resolve) {
            if (document.readyState === 'interactive' || document.readyState === 'complete') {
                resolve();
            }
            else {
                document.addEventListener('DOMContentLoaded', function () {
                    resolve();
                });
            }
        });
    };
    OfficePwa.prototype.clearPwaJumpList = function () {
        var _this_1 = this;
        localStorage.removeItem(OfficePwa.PwaJumpListMappingKey);
        localStorage.removeItem(OfficePwa.PwaJumpListRemovedItemsKey);
        var params = {};
        Windows.UI.StartScreen.JumpList.loadCurrentAsync().then(function (list) {
            list.systemGroupKind = Windows.UI.StartScreen.JumpListSystemGroupKind.none;
            list.items.clear();
            list.saveAsync().then(function () {
                params[OfficePwa.InstrumentationParamState] = 'Success';
                params[OfficePwa.InstrumentationParamAction] = 'SaveJumpListToClear';
                _this_1.logAriaEvent(LoggingUtil.GenericEvent, OfficePwa.InstrumentationEventJumpList, 2 /* Enhanced */, params);
            }, function (error) {
                params[OfficePwa.InstrumentationParamError] = (error || '').toString();
                params[OfficePwa.InstrumentationParamAction] = 'SaveJumpListToClear';
                _this_1.logAriaEvent(LoggingUtil.GenericEvent, OfficePwa.InstrumentationEventJumpList, 2 /* Enhanced */, params);
            });
        }, function (error) {
            params[OfficePwa.InstrumentationParamError] = (error || '').toString();
            params[OfficePwa.InstrumentationParamAction] = 'LoadJumpListToClear';
            _this_1.logAriaEvent(LoggingUtil.GenericEvent, OfficePwa.InstrumentationEventJumpList, 2 /* Enhanced */, params);
        });
    };
    OfficePwa.prototype.setupCurrentUser = function (authority, userId) {
        window.pwaCurrentUserId = this.getUserIdKey(authority, userId);
        this.getCurrentUserPhoto();
    };
    OfficePwa.prototype.getAppWorkloadType = function () {
        if (this.isUwp) {
            if (typeof (window.Office) !== 'undefined') {
                return 'WebView2';
            }
            else {
                var webView2Version = new RegExp('1[0-9]{2}10');
                var wrapperVersion = Windows.ApplicationModel.Package.current.id.version;
                if ((wrapperVersion === null || wrapperVersion === void 0 ? void 0 : wrapperVersion.build) && webView2Version.test((wrapperVersion.build).toString())) {
                    // UWP appType launched as a fallback due to WebView2 runtime not installed on end user system
                    return 'UWP_fallback';
                }
                else {
                    return 'UWP';
                }
            }
        }
    };
    OfficePwa.prototype.isValidAppVersion = function (version) {
        if (this.isUwp) {
            if (typeof (window.Office) !== 'undefined') {
                var wrapperVersion = Windows.ApplicationModel.Package.current.id.version;
                // In case of WebView2, isValid if its the same as current version passed or higher
                if (wrapperVersion.minor > version.minor || (wrapperVersion.minor === version.minor && wrapperVersion.build >= version.build)) {
                    return true;
                }
                else {
                    // Not valid for lower versions
                    return false;
                }
            }
            else {
                // UWP or UWP_Fallback
                return true;
            }
        }
        return false;
    };
    OfficePwa.prototype.findAllAccounts = function (authority) {
        return __awaiter(this, void 0, void 0, function () {
            var errorParams, provider, findAllAccountsResult, error_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        errorParams = {};
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, , 5]);
                        return [4 /*yield*/, Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.findAccountProviderAsync(OfficePwa.MsaAadProviderId, authority)];
                    case 2:
                        provider = _a.sent();
                        return [4 /*yield*/, Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager.findAllAccountsAsync(provider, OfficePwa.AppId)];
                    case 3:
                        findAllAccountsResult = _a.sent();
                        if (findAllAccountsResult.status === Windows.Security.Authentication.Web.Core.FindAllWebAccountsStatus.success) {
                            return [2 /*return*/, Promise.resolve(findAllAccountsResult.accounts)];
                        }
                        else {
                            errorParams[OfficePwa.InstrumentationParamError] = findAllAccountsResult.status + ":" + findAllAccountsResult.providerError.toString();
                        }
                        return [3 /*break*/, 5];
                    case 4:
                        error_2 = _a.sent();
                        errorParams[OfficePwa.InstrumentationParamError] = (error_2 || 'Error when calling FindAllAccountsAsync').toString();
                        return [3 /*break*/, 5];
                    case 5:
                        //ToDo: Prgollui- Log to OTEL
                        this.logAriaEvent(LoggingUtil.GenericEvent, 'Pwa_FindAllAccounts_Error', 0 /* Security */, errorParams);
                        return [2 /*return*/, Promise.resolve([])];
                }
            });
        });
    };
    OfficePwa.AadAuthority = 'organizations';
    OfficePwa.AppId = '0ec893e0-5785-4de6-99da-4ed124e5296c';
    OfficePwa.BasePwaPath = '/pwa';
    OfficePwa.CurrentAccountIdKey = 'CurrentAccountId';
    OfficePwa.CurrentAuthorityKey = 'CurrentAuthority';
    OfficePwa.CurrentUserProviderIdKey = 'CurrentUserProviderId';
    OfficePwa.DiskPreloadStorageKey = 'PwaDiskPreloadUrlCache';
    OfficePwa.ErrorPageId = 'error-page';
    OfficePwa.ExistingSessionKey = 'ExistingSession';
    OfficePwa.IconFontElementId = 'icon-font-bundle';
    OfficePwa.InitialPerformanceEvent = 'Pwa_Init';
    OfficePwa.InstrumentationEventJumpList = 'Pwa_Jump_List';
    OfficePwa.InstrumentationEventJumpListItemClick = 'Pwa_Jump_List_Item_Click';
    OfficePwa.InstrumentationEventOnActivate = 'Pwa_On_Activated';
    OfficePwa.InstrumentationParamAction = 'Action';
    OfficePwa.InstrumentationParamError = 'Error';
    OfficePwa.InstrumentationParamState = 'State';
    OfficePwa.LocalProviderId = 'https://login.windows.local';
    OfficePwa.PrelaunchLogsSamplingRate = 0.005;
    OfficePwa.PwaPage = 'Pwa';
    OfficePwa.MsaAadProviderId = 'https://login.microsoft.com';
    OfficePwa.MsaAuthority = 'consumers';
    OfficePwa.OfficeApiResource = 'OfficeApi';
    OfficePwa.OfficeHomeId = 'officeHome';
    OfficePwa.PrelaunchModeKey = 'PrelaunchMode';
    OfficePwa.PwaJumpListMappingKey = 'PwaJumpListMapping';
    OfficePwa.PwaJumpListRemovedItemsKey = 'PwaJumpListRemovedItems';
    OfficePwa.PwaOnActivatePendingEventArgsKey = 'PwaOnActivatePendingEventArgs';
    OfficePwa.ReactShellHeaderId = 'ReactShellHeader';
    OfficePwa.PwaStartupConfigElementId = 'pwaStartupConfigJson';
    OfficePwa.SignedOutKey = 'UserSignedOut';
    OfficePwa.SignOutWindowName = 'logout';
    OfficePwa.SignOutWindowNameKey = 'LogoutWindowName';
    OfficePwa.SwitchAccountKey = 'SwitchAccount';
    OfficePwa.SwitchToSelectedAccountKey = 'SwitchSelectedAccount';
    OfficePwa.ThirdPartyNoticeFileName = 'ThirdPartyNotice.html';
    OfficePwa.ToastMessageKey = '{TOASTMSG}';
    OfficePwa.UnauthPageId = 'unauth-page';
    OfficePwa.VersionUpdatePageId = 'version-update-page';
    OfficePwa.OfficeHomeV2Resource = '4765445b-32c6-49b0-83e6-1d93765276ca/.default';
    // Friendly names need to be kept in sync with OfficePwaApiModel.cs, and AccessTokenConsumer enum in access-token.service.ts
    OfficePwa.V2ResourceFriendlyName = ['OfficeHome', 'OfficeApi', 'Graph', 'Substrate'];
    return OfficePwa;
}());
export { OfficePwa };
